import { useSnackbar, SnackbarKey } from "notistack";
import { useEffect, useCallback, useRef } from "react";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import type { ReactNode } from "react";

import {
  SNACK_BARS_SEVERITY_SUCCESS,
  SNACK_BARS_SEVERITY_ERROR,
} from "../../constants";

interface Props {
  open: boolean;
  message?: ReactNode;
  severity?: string;
  autoHide?: boolean;
  autoHideDuration?: number;
  handleClose?: (() => void) | null;
  width?: number | string;
  top?: string | number;
  anchorOriginVertical?: string;
  anchorOriginHorizontal?: string;
}

const SbuxStackSnackbar = ({
  open,
  message,
  severity = SNACK_BARS_SEVERITY_SUCCESS,
  autoHide = true,
  autoHideDuration = 6000,
  handleClose = null,
  width = 400,
}: Props) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const theme = useTheme();
  const activeSnackbarKey = useRef<SnackbarKey | null>(null);
  const forceCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleCloseRef = useRef(handleClose);
  handleCloseRef.current = handleClose;

  const onSnackbarClose = useCallback(
    (snackbarKey: SnackbarKey) =>
      (_event: unknown, _reason = "unknown") => {
        if (_reason === "clickaway") {
          return;
        }

        closeSnackbar(snackbarKey);

        if (typeof handleCloseRef.current === "function") {
          handleCloseRef.current();
        }
      },
    [closeSnackbar],
  );

  const renderCloseButton = useCallback(
    (snackbarKey: SnackbarKey) => (
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={() => {
          onSnackbarClose(snackbarKey)(null, "iconClick");
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    ),
    [onSnackbarClose],
  );

  useEffect(() => {
    if (activeSnackbarKey.current) {
      closeSnackbar(activeSnackbarKey.current);
    }

    if (open && message) {
      const persist = !autoHide || severity === SNACK_BARS_SEVERITY_ERROR;
      let variant: "success" | "error" = "success";
      let bgColor = theme.palette.success.main;
      let textColor = theme.palette.success.contrastText;

      if (severity === SNACK_BARS_SEVERITY_ERROR) {
        variant = "error";
        bgColor = theme.palette.error.main;
        textColor = theme.palette.error.contrastText;
      }

      const key = enqueueSnackbar(message, {
        variant,
        persist,
        autoHideDuration: persist ? null : autoHideDuration,
        onClose: onSnackbarClose as Parameters<
          typeof enqueueSnackbar
        >[1] extends { onClose?: infer T }
          ? T
          : never,
        action: renderCloseButton,
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
        style: {
          maxWidth: width,
          backgroundColor: bgColor,
          color: textColor,
          fontSize: "0.90rem",
          display: "flex",
          flexWrap: "nowrap",
          alignItems: "center",
        },

        preventDuplicate: true,
      });

      activeSnackbarKey.current = key;

      if (!persist && autoHide && typeof autoHideDuration === "number") {
        if (forceCloseTimer.current) {
          clearTimeout(forceCloseTimer.current);
        }
        forceCloseTimer.current = setTimeout(() => {
          onSnackbarClose(key)(null, "timeout");
        }, autoHideDuration);
      }
    }

    return () => {
      if (activeSnackbarKey.current) {
        closeSnackbar(activeSnackbarKey.current);
        activeSnackbarKey.current = null;
      }
      if (forceCloseTimer.current) {
        clearTimeout(forceCloseTimer.current);
        forceCloseTimer.current = null;
      }
    };
  }, [
    open,
    message,
    severity,
    autoHide,
    autoHideDuration,
    enqueueSnackbar,
    closeSnackbar,
    onSnackbarClose,
    renderCloseButton,
    theme,
    width,
  ]);

  return null;
};

export default SbuxStackSnackbar;
